import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  createFooterForm,
  FOOTER_API_BASE,
  normalizeFooterData,
  toFooterAssetUrl,
} from "../../../utils/footerConfig";

const EMPTY_SECTION = { title: "", links: [{ label: "", path: "/" }] };
const EMPTY_LINK = { label: "", path: "/" };
const EMPTY_CONTACT = { type: "custom", value: "", href: "" };
const EMPTY_SOCIAL = { platform: "facebook", url: "" };

export default function FooterSettingsAdmin() {
  const [form, setForm] = useState(() => createFooterForm());
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(FOOTER_API_BASE)
      .then((res) => {
        setForm(normalizeFooterData(res.data?.data));
      })
      .catch((err) => {
        console.error("Failed to load footer settings:", err);
      });
  }, []);

  const primaryLogoPreview = useMemo(() => {
    if (form.primaryLogoFile) {
      return URL.createObjectURL(form.primaryLogoFile);
    }
    return toFooterAssetUrl(form.primaryLogoUrl);
  }, [form.primaryLogoFile, form.primaryLogoUrl]);

  const secondaryLogoPreview = useMemo(() => {
    if (form.secondaryLogoFile) {
      return URL.createObjectURL(form.secondaryLogoFile);
    }
    return toFooterAssetUrl(form.secondaryLogoUrl);
  }, [form.secondaryLogoFile, form.secondaryLogoUrl]);

  useEffect(() => {
    return () => {
      if (form.primaryLogoFile && primaryLogoPreview) {
        URL.revokeObjectURL(primaryLogoPreview);
      }
      if (form.secondaryLogoFile && secondaryLogoPreview) {
        URL.revokeObjectURL(secondaryLogoPreview);
      }
    };
  }, [
    form.primaryLogoFile,
    form.secondaryLogoFile,
    primaryLogoPreview,
    secondaryLogoPreview,
  ]);

  const handleField = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setForm((prev) => ({
        ...prev,
        [name]: files[0] || null,
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateSection = (index, field, value) => {
    setForm((prev) => {
      const linkSections = [...prev.linkSections];
      linkSections[index] = { ...linkSections[index], [field]: value };
      return { ...prev, linkSections };
    });
  };

  const addSection = () => {
    setForm((prev) => ({
      ...prev,
      linkSections: [...prev.linkSections, { ...EMPTY_SECTION }],
    }));
  };

  const removeSection = (index) => {
    setForm((prev) => ({
      ...prev,
      linkSections: prev.linkSections.filter((_, sectionIndex) => sectionIndex !== index),
    }));
  };

  const addSectionLink = (sectionIndex) => {
    setForm((prev) => {
      const linkSections = [...prev.linkSections];
      linkSections[sectionIndex] = {
        ...linkSections[sectionIndex],
        links: [...linkSections[sectionIndex].links, { ...EMPTY_LINK }],
      };
      return { ...prev, linkSections };
    });
  };

  const updateSectionLink = (sectionIndex, linkIndex, field, value) => {
    setForm((prev) => {
      const linkSections = [...prev.linkSections];
      const links = [...linkSections[sectionIndex].links];
      links[linkIndex] = { ...links[linkIndex], [field]: value };
      linkSections[sectionIndex] = { ...linkSections[sectionIndex], links };
      return { ...prev, linkSections };
    });
  };

  const removeSectionLink = (sectionIndex, linkIndex) => {
    setForm((prev) => {
      const linkSections = [...prev.linkSections];
      const nextLinks = linkSections[sectionIndex].links.filter(
        (_, currentIndex) => currentIndex !== linkIndex
      );
      linkSections[sectionIndex] = {
        ...linkSections[sectionIndex],
        links: nextLinks.length ? nextLinks : [{ ...EMPTY_LINK }],
      };
      return { ...prev, linkSections };
    });
  };

  const addContactItem = () => {
    setForm((prev) => ({
      ...prev,
      contactItems: [...prev.contactItems, { ...EMPTY_CONTACT }],
    }));
  };

  const updateContactItem = (index, field, value) => {
    setForm((prev) => {
      const contactItems = [...prev.contactItems];
      contactItems[index] = { ...contactItems[index], [field]: value };
      return { ...prev, contactItems };
    });
  };

  const removeContactItem = (index) => {
    setForm((prev) => ({
      ...prev,
      contactItems: prev.contactItems.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const addSocialLink = () => {
    setForm((prev) => ({
      ...prev,
      socialLinks: [...prev.socialLinks, { ...EMPTY_SOCIAL }],
    }));
  };

  const updateSocialLink = (index, field, value) => {
    setForm((prev) => {
      const socialLinks = [...prev.socialLinks];
      socialLinks[index] = { ...socialLinks[index], [field]: value };
      return { ...prev, socialLinks };
    });
  };

  const removeSocialLink = (index) => {
    setForm((prev) => ({
      ...prev,
      socialLinks: prev.socialLinks.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSaved(false);
    setError("");

    try {
      const formData = new FormData();
      formData.append("aboutText", form.aboutText);
      formData.append("trustText", form.trustText);
      formData.append("contactTitle", form.contactTitle);
      formData.append("copyrightText", form.copyrightText);

      if (form.primaryLogoFile) {
        formData.append("primaryLogo", form.primaryLogoFile);
      } else {
        formData.append("primaryLogoUrl", form.primaryLogoUrl);
      }

      if (form.secondaryLogoFile) {
        formData.append("secondaryLogo", form.secondaryLogoFile);
      } else {
        formData.append("secondaryLogoUrl", form.secondaryLogoUrl);
      }

      formData.append(
        "linkSections",
        JSON.stringify(
          form.linkSections.map((section) => ({
            title: section.title,
            links: section.links.filter((link) => link.label.trim()),
          }))
        )
      );

      formData.append(
        "contactItems",
        JSON.stringify(form.contactItems.filter((item) => item.value.trim()))
      );

      formData.append(
        "socialLinks",
        JSON.stringify(
          form.socialLinks.filter(
            (item) => item.platform.trim() && item.url.trim()
          )
        )
      );

      formData.append(
        "keywords",
        JSON.stringify(
          form.keywordsText
            .split("\n")
            .map((item) => item.trim())
            .filter(Boolean)
        )
      );

      await axios.put(FOOTER_API_BASE, formData);
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save footer settings.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container pt-5 mt-5" style={{ maxWidth: 1150 }}>
      <h3 className="mb-1">Footer Settings</h3>
      <p className="text-muted small mb-4">
        Manage the public website footer from this page. Every label, link, logo,
        keyword, contact item, and social URL is editable here.
      </p>

      {error && <div className="alert alert-danger">{error}</div>}
      {saved && <div className="alert alert-success">Footer saved successfully.</div>}

      <form onSubmit={handleSubmit} className="card p-4 shadow-sm">
        <h5 className="mb-3">Brand And Description</h5>
        <div className="row">
          <div className="col-12 mb-3">
            <label className="form-label">About Text</label>
            <textarea
              name="aboutText"
              className="form-control"
              rows="4"
              value={form.aboutText}
              onChange={handleField}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Trust Text</label>
            <input
              name="trustText"
              className="form-control"
              value={form.trustText}
              onChange={handleField}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Contact Section Title</label>
            <input
              name="contactTitle"
              className="form-control"
              value={form.contactTitle}
              onChange={handleField}
            />
          </div>
        </div>

        <h5 className="mt-4 pt-3 border-top">Logos</h5>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Primary Logo URL</label>
            <input
              name="primaryLogoUrl"
              className="form-control"
              value={form.primaryLogoUrl}
              onChange={handleField}
              placeholder="https://..."
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Upload Primary Logo</label>
            <input
              type="file"
              name="primaryLogoFile"
              className="form-control"
              accept="image/*"
              onChange={handleField}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Secondary Logo URL</label>
            <input
              name="secondaryLogoUrl"
              className="form-control"
              value={form.secondaryLogoUrl}
              onChange={handleField}
              placeholder="https://..."
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Upload Secondary Logo</label>
            <input
              type="file"
              name="secondaryLogoFile"
              className="form-control"
              accept="image/*"
              onChange={handleField}
            />
          </div>
          {primaryLogoPreview && (
            <div className="col-md-6 mb-3">
              <label className="form-label d-block">Primary Preview</label>
              <img
                src={primaryLogoPreview}
                alt="Primary footer logo preview"
                style={{
                  height: 80,
                  maxWidth: "100%",
                  objectFit: "contain",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  padding: 8,
                }}
              />
            </div>
          )}
          {secondaryLogoPreview && (
            <div className="col-md-6 mb-3">
              <label className="form-label d-block">Secondary Preview</label>
              <img
                src={secondaryLogoPreview}
                alt="Secondary footer logo preview"
                style={{
                  height: 80,
                  maxWidth: "100%",
                  objectFit: "contain",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  padding: 8,
                }}
              />
            </div>
          )}
        </div>

        <div className="mt-4 pt-3 border-top">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1">Link Sections</h5>
              <p className="text-muted small mb-0">
                These create the middle footer columns like Quick Links, Courses,
                and Retreats.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={addSection}
            >
              + Add Section
            </button>
          </div>

          {form.linkSections.map((section, sectionIndex) => (
            <div className="border rounded p-3 mb-3" key={`section-${sectionIndex}`}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <strong>
                  Section {sectionIndex + 1}
                  {section.title ? ` - ${section.title}` : ""}
                </strong>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => removeSection(sectionIndex)}
                >
                  Remove Section
                </button>
              </div>

              <div className="mb-3">
                <label className="form-label">Section Title</label>
                <input
                  className="form-control"
                  value={section.title}
                  onChange={(e) => updateSection(sectionIndex, "title", e.target.value)}
                />
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label mb-0">Section Links</label>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => addSectionLink(sectionIndex)}
                >
                  + Add Link
                </button>
              </div>

              {section.links.map((link, linkIndex) => (
                <div className="row align-items-end mb-2" key={`link-${sectionIndex}-${linkIndex}`}>
                  <div className="col-md-5">
                    <label className="form-label">Label</label>
                    <input
                      className="form-control"
                      value={link.label}
                      onChange={(e) =>
                        updateSectionLink(sectionIndex, linkIndex, "label", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label">Path</label>
                    <input
                      className="form-control"
                      value={link.path}
                      onChange={(e) =>
                        updateSectionLink(sectionIndex, linkIndex, "path", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-md-2">
                    <button
                      type="button"
                      className="btn btn-outline-danger w-100"
                      onClick={() => removeSectionLink(sectionIndex, linkIndex)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="mt-4 pt-3 border-top">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1">Contact Items</h5>
              <p className="text-muted small mb-0">
                Add location, phone, email, timing, or any custom contact row.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={addContactItem}
            >
              + Add Contact Item
            </button>
          </div>

          {form.contactItems.map((item, index) => (
            <div className="row align-items-end mb-3" key={`contact-${index}`}>
              <div className="col-md-3">
                <label className="form-label">Type</label>
                <select
                  className="form-select"
                  value={item.type}
                  onChange={(e) => updateContactItem(index, "type", e.target.value)}
                >
                  <option value="location">Location</option>
                  <option value="phone">Phone</option>
                  <option value="email">Email</option>
                  <option value="clock">Clock</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Value</label>
                <input
                  className="form-control"
                  value={item.value}
                  onChange={(e) => updateContactItem(index, "value", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Optional Link</label>
                <input
                  className="form-control"
                  value={item.href}
                  placeholder="tel:, mailto:, https://..."
                  onChange={(e) => updateContactItem(index, "href", e.target.value)}
                />
              </div>
              <div className="col-md-1">
                <button
                  type="button"
                  className="btn btn-outline-danger w-100"
                  onClick={() => removeContactItem(index)}
                >
                  X
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-3 border-top">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1">Social Links</h5>
              <p className="text-muted small mb-0">
                Supported icons include Facebook, Instagram, and YouTube. Other
                names still save as custom links.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={addSocialLink}
            >
              + Add Social Link
            </button>
          </div>

          {form.socialLinks.map((item, index) => (
            <div className="row align-items-end mb-3" key={`social-${index}`}>
              <div className="col-md-3">
                <label className="form-label">Platform</label>
                <input
                  className="form-control"
                  value={item.platform}
                  onChange={(e) => updateSocialLink(index, "platform", e.target.value)}
                />
              </div>
              <div className="col-md-8">
                <label className="form-label">URL</label>
                <input
                  className="form-control"
                  value={item.url}
                  onChange={(e) => updateSocialLink(index, "url", e.target.value)}
                />
              </div>
              <div className="col-md-1">
                <button
                  type="button"
                  className="btn btn-outline-danger w-100"
                  onClick={() => removeSocialLink(index)}
                >
                  X
                </button>
              </div>
            </div>
          ))}
        </div>

        <h5 className="mt-4 pt-3 border-top">Keywords And Bottom Line</h5>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Keywords</label>
            <textarea
              name="keywordsText"
              className="form-control"
              rows="8"
              value={form.keywordsText}
              onChange={handleField}
              placeholder="One keyword or phrase per line"
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Copyright Text</label>
            <textarea
              name="copyrightText"
              className="form-control"
              rows="8"
              value={form.copyrightText}
              onChange={handleField}
            />
            <div className="form-text">
              The current year is added automatically before this text on the website.
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end mt-4">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Saving..." : "Save Footer"}
          </button>
        </div>
      </form>
    </div>
  );
}
